import {
  MainNavItem,
  NavItem,
} from '@app/components/layout/sidebar/components/app-sidebar';
import { NavMain } from '@app/components/layout/sidebar/components/nav-main';
import { NavUser } from '@app/components/layout/sidebar/components/nav-user';
import { AccountFeatures } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Logo from '@shared/components/logo/logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { cn } from '@shared/lib/utils';
import { useGroupContext } from '../group-context';
import { useGroupFilterSearch } from '../hooks/use-group-filters';
import {
  GROUP_ROUTES,
  GroupSectionKey,
  isSectionUnlocked,
} from '../config/pages';

function makeSubItem(
  title: string,
  to: string,
  sectionUnlocked: boolean
): NavItem {
  return {
    title,
    to,
    show: true,
    disabled: !sectionUnlocked,
  };
}

/**
 * `filterSearch` is appended to every route so the global filters (URL
 * search params) survive sidebar navigation between /group/* pages.
 */
function buildNav(
  features: AccountFeatures | null,
  filterSearch: string
): MainNavItem[] {
  const sectionUnlocked = (section: GroupSectionKey) =>
    isSectionUnlocked(section, features);

  return [
    {
      title: 'Group dashboard',
      to: GROUP_ROUTES.DASHBOARD + filterSearch,
      icon: <Icons.dashboard />,
      show: true,
      // `/group` is also the prefix of all other group routes, so without
      // exact matching the dashboard would highlight everywhere.
      exact: true,
    },
    {
      title: 'Company',
      to: '#',
      icon: <Icons.company />,
      show: true,
      isActive: sectionUnlocked('company'),
      disabled: !sectionUnlocked('company'),
      items: [
        makeSubItem(
          'Company emissions',
          GROUP_ROUTES.COMPANY_EMISSIONS + filterSearch,
          sectionUnlocked('company')
        ),
        makeSubItem(
          'Company reports',
          GROUP_ROUTES.COMPANY_REPORTS + filterSearch,
          sectionUnlocked('company')
        ),
      ],
    },
    {
      title: 'Purchased goods',
      to: '#',
      icon: <Icons.shoppingCart />,
      show: true,
      isActive: sectionUnlocked('purchasing'),
      disabled: !sectionUnlocked('purchasing'),
      items: [
        makeSubItem(
          'Purchasing emissions',
          GROUP_ROUTES.PURCHASING_EMISSIONS + filterSearch,
          sectionUnlocked('purchasing')
        ),
        makeSubItem(
          'Food and drink',
          GROUP_ROUTES.PURCHASING_FOOD_AND_DRINK + filterSearch,
          sectionUnlocked('purchasing')
        ),
        makeSubItem(
          'Packaging',
          GROUP_ROUTES.PURCHASING_PACKAGING + filterSearch,
          sectionUnlocked('purchasing')
        ),
      ],
    },
    {
      title: 'Products',
      to: '#',
      icon: <Icons.salad />,
      show: true,
      isActive: sectionUnlocked('products'),
      disabled: !sectionUnlocked('products'),
      items: [
        makeSubItem(
          'All products',
          GROUP_ROUTES.PRODUCTS + filterSearch,
          sectionUnlocked('products')
        ),
        makeSubItem(
          'Collections',
          GROUP_ROUTES.PRODUCTS_COLLECTIONS + filterSearch,
          sectionUnlocked('products')
        ),
      ],
    },
    {
      title: 'Organisations',
      to: GROUP_ROUTES.ORGANIZATIONS + filterSearch,
      icon: <Icons.building />,
      show: true,
    },
  ];
}

export function GroupSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { state } = useSidebar();
  const { groupFeatures } = useGroupContext();
  const filterSearch = useGroupFilterSearch();

  const collapsed = state === 'collapsed';
  const expanded = state === 'expanded';

  const mainNav = buildNav(groupFeatures, filterSearch);

  return (
    <Sidebar
      collapsible="icon"
      className="[--sidebar-accent:#f0f4f7]"
      {...props}
    >
      <SidebarHeader
        className={cn('flex h-24 justify-center bg-primary-800', {
          'pl-5': expanded,
        })}
      >
        {collapsed ? (
          <Logo
            variant="icon"
            color="light"
            className={cn('w-44', {
              'w-8 mx-auto': collapsed,
            })}
          />
        ) : (
          <Logo
            variant="name"
            color="light"
            className={cn('w-44', {
              'w-8 mx-auto': collapsed,
            })}
          />
        )}
      </SidebarHeader>
      <SidebarContent
        className={cn('px-2', {
          'mx-auto': collapsed,
        })}
      >
        <NavMain items={mainNav} />
      </SidebarContent>
      <SidebarFooter
        className={cn('px-2', {
          'mx-auto': collapsed,
        })}
      >
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
